// No third-party import: `server/middleware/document-link.ts` bundles this file into the server.
import { DocumentLinkKindEnum } from "~/utils/enums/documents.enums";
import type { IDocumentLinkTarget } from "~/utils/interfaces/session-notifications-interfaces";

/** Public orders routes a partner-mail link may wrap, with the query parameter each one reads. */
const DOCUMENT_LINK_ROUTES: Record<DocumentLinkKindEnum, { path: string; param: string }> = {
  [DocumentLinkKindEnum.ATTENDEE_LIST]: { path: "/session-notifications/download-attendee-list", param: "id" },
  [DocumentLinkKindEnum.DOCUMENT]: { path: "/documents/getSignedUrlFromProtectedLink", param: "token" },
};

/**
 * Reads `document_link`, the orders URL the partner mail carries in base64. Only the route and its
 * credential are kept, so nothing ever calls, nor sends the partner to, a host the link names.
 */
export function documentLinkTarget(documentLink: unknown): IDocumentLinkTarget | null {
  if (typeof documentLink !== "string" || !documentLink) return null;

  let url: URL;
  try {
    // Query parsers read a raw `+` as a space; the base64url alphabet is accepted too.
    const base64 = documentLink.replace(/ /g, "+").replace(/-/g, "+").replace(/_/g, "/");
    url = new URL(new TextDecoder().decode(Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))));
  } catch {
    return null;
  }

  for (const kind of Object.values(DocumentLinkKindEnum)) {
    const { path, param } = DOCUMENT_LINK_ROUTES[kind];
    const credential = url.searchParams.get(param);
    if (url.pathname.endsWith(path) && credential) return { kind, credential };
  }
  return null;
}

/** The orders route a valid link downloads from, on this app's own API host. */
export function documentLinkDownloadUrl(ordersUrl: string, target: IDocumentLinkTarget): string {
  const { path, param } = DOCUMENT_LINK_ROUTES[target.kind];
  return `${ordersUrl}${path}?${param}=${encodeURIComponent(target.credential)}`;
}
